import axiosInstance from "./axiosService";
import axios from "axios";

// interfaces.ts
interface Office {
  address: string;
  city: string;
  phone_number: string;
  state: string;
  zip_code: string;
  pkid: string;
  id?: string;
}
interface Clinic {
  id: number;
  name: string;
  clinic_offices: Office[];
}
export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  // Add more fields as needed
}

export interface Driver {
  id: string;
  name: string;
  vehicle: string;
  // Add more fields as needed
}

export interface Appointment {
  id: string;
  patientName: string;
  date: string;
  time: string;
  // Add more fields as needed
}

export interface Document {
  id: string;
  title: string;
  type: string;
  // Add more fields as needed
}

// ---------------Get CLinic-----------------------
export const getClinics = async (): Promise<Clinic[]> => {
  const response = await axiosInstance.get(`/clinic/clinic/`);
  return response.data;
};

export const getClinicUserConnectedClinics = async (): Promise<Clinic[]> => {
  const response = await axiosInstance.get(`/clinic/clinic/user-conected/`);
  return response.data;
};

export const updateClinics = async (
  clinicId: string,
  updatedData: Partial<Clinic>
): Promise<Clinic> => {
  try {
    const response = await axiosInstance.patch(
      `/clinic/clinic/${clinicId}/`,
      updatedData
    );
    return response.data;
  } catch (error) {
    console.error(`Error updating clinic with ID ${clinicId}:`, error);
    throw error; // Re-throw error to handle it in the calling function
  }
};

export const getClinicsbyID = async (clinicId: string): Promise<Office[]> => {
  const response = await axiosInstance.get(`/clinic/clinic/${clinicId}`);
  return response.data;
};

export const getClinicsbyID2 = async (
  clinicId: string
): Promise<{ name: string }> => {
  const response = await axiosInstance.get(`/clinic/clinic/${clinicId}`);
  return response.data;
};

export const createClinic = async (
  data: Record<string, any>
): Promise<void> => {
  const response = await axiosInstance.post(`/clinic/clinic/`, data);
  return response.data;
};

// ---------------Offices CRUD-----------------------
export const getClinicOffices = async (): Promise<Office[]> => {
  const response = await axiosInstance.get(`/clinic/clinic/offices/`);
  return response.data;
};

export const getClinicOfficeById = async (
  officeId: string
): Promise<Office> => {
  const response = await axiosInstance.get(
    `/clinic/clinic/offices/${officeId}/`
  );
  return response.data;
};

export const createClinicOffice = async (
  officeData: Office
): Promise<Office> => {
  const response = await axiosInstance.post(
    `/clinic/clinic/offices/`,
    officeData
  );
  return response.data;
};

export const updateClinicOffice = async (
  officeId: string,
  officeData: Office
): Promise<Office> => {
  const response = await axiosInstance.put(
    `/clinic/clinic/offices/${officeId}/`,
    officeData
  );
  return response.data;
};

export const deleteClinicOffice = async (officeId: string): Promise<Office> => {
  const response = await axiosInstance.delete(
    `/clinic/clinic/offices/${officeId}/`
  );
  return response.data;
};

//----------------------- Users CRUD
export const getClinicUsers = async (): Promise<any[]> => {
  const response = await axiosInstance.get(`clinic/clinics-user/`);
  return response.data;
};

// Create a new user
export const inviteClinicUser = async (userData: {
  email: string;
  role: string;
  permissions: string[];
}): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `clinic/clinic/invite-user/`,
      userData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating survey:", error);
    throw error;
  }
};

export const inviteDriverUser = async (userData: {
  email?: string;
  role: string;
  type?: string;
  phone_number?: string;
}): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `clinic/clinic/invite-driver/`,
      userData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating survey:", error);
    throw error;
  }
};

export const bulkInviteDrivers = async (data: { drivers: { email: string; phone_number?: string }[] }): Promise<any> => {
  try {
    const response = await axiosInstance.post(
      `clinic/clinic/bulk-invite-driver/`,
      data
    );
    return response.data;
  } catch (error) {
    console.error("Error in bulk invitation:", error);
    throw error;
  }
};

// Update an existing user
export const updateClinicUser = async (userData: any): Promise<any> => {
  try {
    const response = await axiosInstance.put(
      `/clinic/clinics-user/update/`,
      userData
    );
    return response.data;
  } catch (error: any) {
    console.error(
      "Error updating clinic user:",
      error?.response?.data || error.message
    );
    throw error;
  }
};

// Delete a user
export const deleteClinicUser = async (userClinicId: number): Promise<void> => {
  const response = await axiosInstance.delete(
    `clinic/clinic/clinics-user/${userClinicId}/`
  );
  return response.data;
};

// ---------Surveys----------
/**
 * Universal function to fetch survey .
 * @returns Promise<any> - API response data.
 */
export const getConnectedSurveys = async (selected: string, search: string = ""): Promise<any[]> => {
  try {
    // Send the query string 'selected' directly as a query parameter
    const response = await axiosInstance.get("clinic/clinic/surveys/", {
      params: { selected, search },
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching surveys:", error);
    throw error;
  }
};
/**
 * Universal function to update a survey with dynamic data.
 * @param surveyId - ID of the survey to update.
 * @param payload - Object containing the data to update (e.g., MA, Provider, etc.).
 * @returns Promise<any> - API response data.
 */
export const updateSurvey = async (
  surveyId: string,
  data: Record<string, any>
): Promise<any> => {
  try {
    // Make the PUT request to the API with the dynamic payload
    const response = await axiosInstance.put(
      `/driver/driver/physical/${surveyId}`,
      data
    );

    return response.data;
  } catch (error) {
    console.error("Error updating survey:", error);
    throw error;
  }
};

// Drivers CRUD
export const getClinicDrivers = async (clinicId: string): Promise<Driver[]> => {
  const response = await axiosInstance.get(
    `/clinic/clinic/${clinicId}/drivers`
  );
  return response.data;
};

export const getClinicDriverById = async (
  clinicId: string,
  driverId: string
): Promise<Driver> => {
  const response = await axiosInstance.get(
    `/clinic/clinic/${clinicId}/drivers/${driverId}`
  );
  return response.data;
};

/**
 * Fetches medical details (surgeries and medications) for a specific driver.
 *
 * @param {string} driverId - The ID of the driver to fetch medical details for. If not provided, it defaults to the authenticated user.
 * @returns {Promise<any>} - A Promise that resolves to the driver's medical details (surgeries & medications).
 * @throws {Error} - Throws an error if the request fails.
 */
export const getDriverMedicalDetails = async (
  driverId?: string
): Promise<any> => {
  try {
    // Make a GET request to fetch the medical details, passing driverId as a query parameter if provided
    const response = await axiosInstance.get("driver/driver/medical-details", {
      params: driverId ? { driver_id: driverId } : {}, // Only add driver_id if provided
    });

    return response.data;
  } catch (error) {
    console.error("Error fetching driver medical details:", error);
    throw error; // Re-throw error for handling at the calling function
  }
};

export const createClinicDriver = async (
  clinicId: string,
  driverData: Driver
): Promise<Driver> => {
  const response = await axiosInstance.post(
    `/clinic/clinic/${clinicId}/drivers`,
    driverData
  );
  return response.data;
};

export const updateClinicDriver = async (
  clinicId: string,
  driverId: string,
  driverData: Driver
): Promise<Driver> => {
  const response = await axiosInstance.put(
    `/clinic/clinic/${clinicId}/drivers/${driverId}`,
    driverData
  );
  return response.data;
};

export const deleteClinicDriver = async (
  clinicId: string,
  driverId: string
): Promise<Driver> => {
  const response = await axiosInstance.delete(
    `/clinic/clinic/${clinicId}/drivers/${driverId}`
  );
  return response.data;
};

// Appointments CRUD
export const getClinicAppointments = async (
  clinicId: string
): Promise<Appointment[]> => {
  const response = await axiosInstance.get(
    `/clinic/clinic/${clinicId}/appointments`
  );
  return response.data;
};

export const getClinicAppointmentById = async (
  clinicId: string,
  appointmentId: string
): Promise<Appointment> => {
  const response = await axiosInstance.get(
    `/clinic/clinic/${clinicId}/appointments/${appointmentId}`
  );
  return response.data;
};

export const createClinicAppointment = async (
  clinicId: string,
  appointmentData: Appointment
): Promise<Appointment> => {
  const response = await axiosInstance.post(
    `/clinic/clinic/${clinicId}/appointments`,
    appointmentData
  );
  return response.data;
};

export const updateClinicAppointment = async (
  clinicId: string,
  appointmentId: string,
  appointmentData: Appointment
): Promise<Appointment> => {
  const response = await axiosInstance.put(
    `/clinic/clinic/${clinicId}/appointments/${appointmentId}`,
    appointmentData
  );
  return response.data;
};

export const deleteClinicAppointment = async (
  clinicId: string,
  appointmentId: string
): Promise<Appointment> => {
  const response = await axiosInstance.delete(
    `/clinic/clinic/${clinicId}/appointments/${appointmentId}`
  );
  return response.data;
};

// Documents CRUD
export const getClinicDocuments = async (
  clinicId: string
): Promise<Document[]> => {
  const response = await axiosInstance.get(
    `/clinic/clinic/${clinicId}/documents`
  );
  return response.data;
};

export const getClinicDocumentById = async (
  clinicId: string,
  documentId: string
): Promise<Document> => {
  const response = await axiosInstance.get(
    `/clinic/clinic/${clinicId}/documents/${documentId}`
  );
  return response.data;
};

export const createClinicDocument = async (
  clinicId: string,
  documentData: Document
): Promise<Document> => {
  const response = await axiosInstance.post(
    `/clinic/clinic/${clinicId}/documents`,
    documentData
  );
  return response.data;
};

export const updateClinicDocument = async (
  clinicId: string,
  documentId: string,
  documentData: Document
): Promise<Document> => {
  const response = await axiosInstance.put(
    `/clinic/clinic/${clinicId}/documents/${documentId}`,
    documentData
  );
  return response.data;
};

export const deleteClinicDocument = async (
  clinicId: string,
  documentId: string
): Promise<Document> => {
  const response = await axiosInstance.delete(
    `/clinic/clinic/${clinicId}/documents/${documentId}`
  );
  return response.data;
};

export const assignProviderClinicEmail = async (payload: {
  email: string;
  user_id: string;
  clinic_id: number;
}): Promise<Document> => {
  const response = await axiosInstance.post(
    "clinic/clinic/add-provider/",
    payload
  );
  return response.data;
};

export const assignProviderClinic = async (payload: {
  user_id: number;
  clinic_id: number;
}): Promise<Document> => {
  const response = await axiosInstance.post(
    "clinic/clinic/assign_clinic_offices/",
    payload
  );
  return response.data;
};

export const createGovernmentFiles = async (data: FormData): Promise<any> => {
  try {
    const response = await axiosInstance.post("/clinic/clinic/forms/", data, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error: any) {
    // Handle error responses
    console.error(
      "Error uploading file:",
      error.response?.data || error.message
    );
    throw error.response?.data || error;
  }
};

export const fetchGovernmentFiles = async (): Promise<any> => {
  try {
    const response = await axiosInstance.get("/clinic/clinic/forms/", {});
    return response.data;
  } catch (error: any) {
    // Handle error responses
    console.error(
      "Error fetching Files:",
      error.response?.data || error.message
    );
    throw error.response?.data || error;
  }
};

export const getPublicClinicDetail = async (clinicId: string): Promise<any> => {
  try {
    const response = await axiosInstance.get(`/clinic/clinic/public/${clinicId}/`);
    return response.data;
  } catch (error: any) {
    console.error("Error fetching public clinic detail:", error.response?.data || error.message);
    throw error.response?.data || error;
  }
};

export const getClinicAnalytics = async (time_filter: string, start_date?: string, end_date?: string) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data } = await axiosInstance.get(`/clinic/clinic/analytics/`, {
    params: { time_filter, tz, ...(start_date && { start_date }), ...(end_date && { end_date }) },
  });
  return data;
};

export const getClinicReportPreview = async (report_type: string, time_filter: string, start_date?: string, end_date?: string) => {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const { data } = await axiosInstance.get(`/clinic/clinic/report-preview/`, {
    params: { report_type, time_filter, tz, ...(start_date && { start_date }), ...(end_date && { end_date }) },
  });
  return data;
};
